import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Skeleton } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useFinancialContexts } from '../contexts/ContextsContext';
import { SettleDialog } from '../components/dashboard/SettleDialog';
import { ReceivablesCard } from '../components/dashboard/ReceivablesCard';
import { DashboardHomeSection } from '../components/dashboard/home/DashboardHomeSection';
import { GroupView } from '../components/dashboard/group/GroupView';
import { MonthPicker } from '../components/dashboard/MonthPicker';
import DashboardEmptyState from '../components/dashboard/DashboardEmptyState';
import FirstStepsCard from '../components/dashboard/FirstStepsCard';
import { ContextMembersDialog } from '../components/contexts/ContextMembersDialog';
import { OnboardingWizard } from '../components/onboarding';
import { GuestWelcomeCard } from '../components/auth/GuestWelcomeCard';
import { contextApi, ContextBalances, PairBalance } from '../services/contextApi';
import { transactionApi } from '../services/transactionApi';
import { monthKey } from '../utils/calendar';
import { tokens } from '../theme/tokens';

/**
 * Início. Personal: the month's budget and what is left of it, what is due,
 * the goals, the groups, where the money went, the latest entries. A group:
 * its balances, its month, who paid. The context picker at the top decides
 * which one this is.
 */
const DashboardPage: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const { state } = useAuth();
  const navigate = useNavigate();
  const { contexts, selectedContextId, selectContext } = useFinancialContexts();
  const user = state.user;

  const groups = useMemo(() => contexts.filter((context) => context.type !== 'personal' && context.isActive), [contexts]);
  const selectedGroup = useMemo(() => groups.find((group) => group.id === selectedContextId) ?? null, [groups, selectedContextId]);
  const groupId = selectedGroup?.id;

  // The month in focus: what every card reads. Today's month to start.
  const [month, setMonth] = useState(() => monthKey(new Date()));
  // Personal only: whether there is any entry at all, whatever the month — the empty state otherwise.
  const [hasTransactions, setHasTransactions] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [inviting, setInviting] = useState(false);
  // The expense a link from Fin came to show ("Ver meu painel"), marked in the group's list.
  const [highlightId, setHighlightId] = useState<string | undefined>();
  const [searchParams, setSearchParams] = useSearchParams();

  // ?group=…&tx=…&month=… from that link: open the group, in the expense's
  // month, then tidy the address so a refresh is a plain Início.
  useEffect(() => {
    const group = searchParams.get('group');
    if (!group) return;
    selectContext(group);
    const linkedMonth = searchParams.get('month');
    if (linkedMonth && /^\d{4}-\d{2}$/.test(linkedMonth)) setMonth(linkedMonth);
    setHighlightId(searchParams.get('tx') ?? undefined);
    setSearchParams({}, { replace: true });
  }, [searchParams, selectContext, setSearchParams]);

  // Who owes whom: the group's ledger in a group, every group in the personal view.
  const [balances, setBalances] = useState<ContextBalances | null>(null);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [allBalances, setAllBalances] = useState<ContextBalances[]>([]);
  const [settlingPair, setSettlingPair] = useState<PairBalance | null>(null);
  const [balancesVersion, setBalancesVersion] = useState(0);

  const currency = selectedGroup?.defaultCurrency || user?.defaultCurrency || 'BRL';

  useEffect(() => {
    if (user && !user.onboardingCompleted) setShowOnboarding(true);
  }, [user]);

  useEffect(() => {
    if (!state.isAuthenticated) return undefined;
    let cancelled = false;
    transactionApi
      .getTransactions({ page: 1, limit: 1 })
      .then((result) => {
        if (cancelled) return;
        setHasTransactions(result.total > 0);
        setError(null);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(err.response?.data?.message || t('errors.failedToLoad'));
        setHasTransactions(false);
      });
    return () => {
      cancelled = true;
    };
  }, [state.isAuthenticated, t]);

  useEffect(() => {
    if (!state.isAuthenticated) {
      setBalances(null);
      setAllBalances([]);
      setBalancesLoading(false);
      return undefined;
    }
    let cancelled = false;
    if (groupId) {
      // Loading the same group again keeps its balances on screen until the
      // fresh ones arrive, and when they fail to.
      const keepSameGroup = (current: ContextBalances | null) => (current?.contextId === groupId ? current : null);
      setBalances(keepSameGroup);
      setBalancesLoading(true);
      contextApi
        .getBalances(groupId)
        .then(
          (result) => {
            if (!cancelled) setBalances(result);
          },
          () => {
            if (!cancelled) setBalances(keepSameGroup);
          },
        )
        .finally(() => {
          if (!cancelled) setBalancesLoading(false);
        });
    } else {
      setBalancesLoading(false);
      setBalances(null);
      contextApi
        .getAllBalances()
        .catch(() => [])
        .then((result) => {
          if (!cancelled) setAllBalances(result);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [state.isAuthenticated, groupId, balancesVersion]);

  const reloadBalances = useCallback(() => setBalancesVersion((version) => version + 1), []);

  // The pair being settled lives in the group's ledger or, from the personal view, in one of them.
  const settlingBalances = balances ?? allBalances.find((entry) =>
    entry.pairs.some((pair) => pair.fromUserId === settlingPair?.fromUserId && pair.toUserId === settlingPair?.toUserId),
  ) ?? null;

  const firstName = user?.firstName ? user.firstName.charAt(0).toUpperCase() + user.firstName.slice(1) : '';
  const now = new Date();
  const isCurrentMonth = month === monthKey(now);
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const dayLine = isCurrentMonth
    ? now.getDate() === daysInMonth
      ? t('home.lastDay', { day: now.getDate(), total: daysInMonth })
      : t('home.dayOf', { day: now.getDate(), total: daysInMonth, count: daysInMonth - now.getDate() })
    : t('home.monthClosed');

  const monthPicker = <MonthPicker value={month} onChange={setMonth} />;

  const openTransactions = () => navigate('/transactions');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 2.5 } }}>
      {showOnboarding && <OnboardingWizard open={showOnboarding} onComplete={() => setShowOnboarding(false)} />}

      {error && <Alert severity='error'>{error}</Alert>}

      <GuestWelcomeCard groupName={selectedGroup?.name} transactionId={highlightId} />

      {selectedGroup ? (
        // "Monte o grupo" until the group's first entry, then its dashboard.
        <GroupView
            group={selectedGroup}
            month={month}
            balances={balances}
            currentUserId={user?.id}
            highlightId={highlightId}
            onSettle={setSettlingPair}
            onInvite={() => setInviting(true)}
            onSeeTransactions={openTransactions}
            monthPicker={monthPicker}
            onRefresh={reloadBalances}
            balancesLoading={balancesLoading}
          />
      ) : (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', columnGap: 2, rowGap: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 2, rowGap: 0.25 }}>
              <Box component='h1' sx={{ m: 0, fontFamily: tokens.display, fontSize: { xs: 26, md: 30 }, fontWeight: 700, letterSpacing: '-0.025em' }}>
                {firstName ? t('home.greeting', { name: firstName }) : t('welcome.back')}
              </Box>
              {hasTransactions && <Box component='span' sx={{ fontSize: 14, color: tokens.ink3 }}>{dayLine}</Box>}
            </Box>
            {hasTransactions && monthPicker}
          </Box>

          {hasTransactions === null ? (
            <Skeleton variant='rounded' height={260} sx={{ borderRadius: '22px' }} />
          ) : hasTransactions ? (
            <>
            <FirstStepsCard />
            <DashboardHomeSection
              month={month}
              currency={currency}
              groupsCard={
                <ReceivablesCard
                  all={allBalances}
                  hasGroups={groups.length > 0}
                  currentUserId={user?.id}
                  onOpenGroup={(contextId) => selectContext(contextId)}
                  onSeeGroups={() => navigate('/contexts')}
                  onCreateGroup={() => navigate('/contexts', { state: { createGroup: true } })}
                />
              }
            />
            </>
          ) : (
            // Before the first entry, the first steps are Início; the plain
            // empty state only once they are done or hidden.
            <FirstStepsCard
              fallback={
                <DashboardEmptyState
                  onAddTransaction={() => navigate('/transactions', { state: { newTransaction: true } })}
                  onConnectTelegram={() => navigate('/settings/telegram')}
                  onReplayTutorial={() => setShowOnboarding(true)}
                  isTelegramLinked={Boolean(user?.isTelegramLinked)}
                />
              }
            />
          )}
        </>
      )}

      {settlingBalances && (
        <SettleDialog
          open={Boolean(settlingPair)}
          balances={settlingBalances}
          pair={settlingPair}
          currentUserId={user?.id}
          onClose={() => setSettlingPair(null)}
          onSettled={() => setBalancesVersion((version) => version + 1)}
        />
      )}

      {selectedGroup && (
        <ContextMembersDialog
          open={inviting}
          context={selectedGroup}
          onClose={() => {
            setInviting(false);
            setBalancesVersion((version) => version + 1);
          }}
        />
      )}
    </Box>
  );
};

export default DashboardPage;
